import type { LegalDocument } from './types'

/**
 * Cookie Policy, text verbatim from the client's reference of 2026-10-02.
 * Section 3's "Your Privacy Choices" is a button carrying
 * `data-tt-consent-open`: the consent module (app/utils/consent) opens the
 * preferences dialog for any element with that attribute, so it works inside
 * this static HTML. A material change to this policy bumps
 * `CONSENT_POLICY_VERSION` in app/utils/consent/consent.ts, which asks every
 * visitor again.
 */
export const COOKIE_POLICY: LegalDocument = {
	slug: 'cookies',
	title: 'Cookie Policy.',
	meta: ['Effective date: October 2, 2026', 'Last updated: October 2, 2026'],
	intro:
		'<p>This Cookie Policy explains how TrueTenant Inc. ("TrueTenant," "we," "us," or "our") uses cookies and similar technologies on truetenant.ai and its subdomains (the "Platform"), and how you can control them. It should be read with our <a href="/privacy">Privacy Policy</a> and <a href="/terms">Terms of Service</a>.</p>',
	sections: [
		{
			id: 'ck-1',
			tocLabel: '1. What cookies are',
			heading: '1. What cookies are',
			html: '<p>Cookies are small text files that a website stores on your device. We also use similar technologies, such as browser storage, pixels and scripts. In this policy, "cookies" includes all of them.</p>',
		},
		{
			id: 'ck-2',
			tocLabel: '2. How we use cookies',
			heading: '2. How we use cookies',
			html: `<p>We use three categories of cookies:</p>
<ul><li><b>Essential:</b> keep the Platform working and secure, including sign-in, fraud prevention, payments, identity and bank verification, error monitoring and remembering your cookie choices. These are always on.</li><li><b>Functional:</b> remember settings you choose, such as display preferences and time zone.</li><li><b>Analytics:</b> help us understand how the Platform is used so we can fix problems and improve it. Some of these tools capture how you interact with our pages, such as clicks, scrolling and page navigation, so we can replay visits and see where the site is hard to use. They hide anything you type and never run on identity, bank or payment steps.</li></ul>
<p>Some cookies are set by service providers that help us deliver the Platform, such as our payment, identity and bank verification, error monitoring and analytics providers. You can ask us for a list of these providers at any time.</p>
<p>We do not use advertising cookies.</p>`,
		},
		{
			id: 'ck-3',
			tocLabel: '3. Your choices',
			heading: '3. Your choices',
			html: `<p>When you first visit the Platform, a banner lets you <b>Accept all</b> cookies or <b>Customize</b> your choices. Functional and Analytics cookies are off until you turn them on. You can change your choices at any time by selecting <button type="button" class="tt-legal-choices" data-tt-consent-open>Your Privacy Choices</button> in the footer of any page.</p>
<p>You can also block or delete cookies in your browser settings. Blocking essential cookies will prevent you from signing in or completing an application.</p>`,
		},
		{
			id: 'ck-4',
			tocLabel: '4. Where your information goes',
			heading: '4. Where your information goes',
			html: '<p>Some of our service providers process information outside Canada, including in the United States, where it may be accessible to the authorities of that country. Our <a href="/privacy">Privacy Policy</a> explains how we protect it. We do not sell information collected through cookies.</p>',
		},
		{
			id: 'ck-5',
			tocLabel: '5. Your rights',
			heading: '5. Your rights',
			html: '<p>You have the rights described in our <a href="/privacy">Privacy Policy</a>, including the right to access your personal information and to withdraw consent.</p>',
		},
		{
			id: 'ck-6',
			tocLabel: '6. Changes to this policy',
			heading: '6. Changes to this policy',
			html: '<p>We update this policy when we change how we use cookies. The "Last updated" date above shows when it last changed.</p>',
		},
		{
			id: 'ck-7',
			tocLabel: '7. Contact us',
			heading: '7. Contact us',
			html: '<p class="tt-legal-addr"><b>TrueTenant Inc.</b><br>Attention: Privacy Officer<br>713 Indian Road<br>Mississauga, Ontario L5H 1R3<br>Canada<br>Email: <a href="mailto:support@truetenant.ai">support@truetenant.ai</a></p>',
		},
	],
}
